import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaPuzzlePiece, FaTimes } from 'react-icons/fa';
import Btn from '../../../components/ui/Btn';
import StatusTag from '../../../components/ui/StatusTag';
import Modal from '../../../components/ui/Modal';
import Field, { NativeSelect, TextInput } from '../../../components/ui/Field';
import { toolsService } from '../../../services/toolsService';
import { friendlyError } from '../../../services/agentsService';
import PreToolsEditor from './PreToolsEditor';
import { ToolEditor } from '../../Tools/ToolEditorPage';
import { NewToolModal } from '../../Tools/ToolsPage';
import { useAuth } from '../../../context/AuthContext';
import { emptyDraft } from '../../../utils/toolDefinition';
import { toastSuccess } from '../../../utils/toast';

// Herramientas del agente: cuáles usa (de la biblioteca global o personales suyas) y, para las que lo piden, qué conector de este
// cliente usa cada una. Se guarda en el borrador (config.custom_tools): el agente las usa al publicar.
// `mode="pre"`: solo «Antes de responder» (su propia sección del agente); si no, las herramientas que el modelo puede usar.
export default function AgentToolsPanel({ agent, draft, readOnly, onSave, onSavePre, mode = 'tools' }) {
  const { role } = useAuth();
  const [tools, setTools] = useState([]);
  const [defs, setDefs] = useState({});
  const [connectors, setConnectors] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [creatingTool, setCreatingTool] = useState(false);
  const [editing, setEditing] = useState(null);       // id de la herramienta abierta en el editor (encima de esta pantalla)
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState('');
  const assigned = useMemo(() => (Array.isArray(draft?.config?.custom_tools) ? draft.config.custom_tools : []), [draft]);

  const load = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([toolsService.listTools(), toolsService.listConnectors()]);
      const mine = t.filter((x) => x.scope === 'global' || x.agent_id === agent.id);
      setTools(mine); setConnectors(c); setDefs(await toolsService.publishedDefinitions(mine)); setState('ready');
    } catch (e) { setError(e.code === 'missing_38' ? e.message : friendlyError(e)); setState('error'); }
  }, [agent.id]);
  useEffect(() => { load(); }, [load]);

  const save = async (list) => {
    setBusy(true); setSaveError('');
    try { await onSave(list); } catch (e) { setSaveError(e.message); } finally { setBusy(false); }
  };
  const usable = (kind) => connectors.filter((c) => c.kind === kind && (c.project_id === null || c.project_id === agent.project_id));
  const byId = Object.fromEntries(tools.map((t) => [t.id, t]));
  // Todas las que se le pueden dar (globales y personales suyas), también las que siguen en borrador: se pueden asignar desde ya y el
  // agente las usa en cuanto se publiquen. Antes solo salían las publicadas y una herramienta en borrador parecía no existir.
  const available = tools.filter((t) => t.status !== 'archived' && !assigned.some((a) => a.tool_id === t.id))
    .sort((a, b) => Number(Boolean(b.published_version)) - Number(Boolean(a.published_version)) || a.name.localeCompare(b.name, 'es'));

  if (state === 'loading') return <div aria-busy="true" className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-24 skeleton rounded-2xl" />)}</div>;
  if (state === 'error') return <p role="note" className="rounded-xl border border-caution/30 bg-caution-soft px-4 py-3 text-[13.5px] text-fg max-w-[70ch]">{error}</p>;

  if (mode === 'pre') return <PreToolsEditor agent={agent} draft={draft} tools={tools} defs={defs} connectors={connectors} readOnly={readOnly} onSave={onSavePre} />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Btn variant="primary" icon={FaPlus} onClick={() => setAdding(true)} disabled={readOnly}>Agregar herramienta</Btn>
        <Btn variant="ghost" onClick={() => setCreatingTool(true)} disabled={readOnly}>Crear una herramienta nueva</Btn>
        <span className="ml-auto text-[12.5px] text-fg-faint">Los cambios se guardan en el borrador: publica el agente para usarlos.</span>
      </div>
      {saveError && <p role="alert" className="text-[13px] text-signal">{saveError}</p>}

      {!assigned.length && (
        <div className="rounded-2xl border border-dashed border-fg/15 p-6 text-[14px] text-fg-dim">
          <FaPuzzlePiece aria-hidden className="h-5 w-5 text-fg-faint mb-2" />
          Este agente todavía no tiene herramientas propias. Siempre puede pasar la conversación a una persona (eso se ajusta en <Link to={`/agentes/${agent.id}/comportamiento`} className="text-live hover:underline">Comportamiento</Link>).
        </div>
      )}

      <ul className="space-y-3">
        {assigned.map((a, i) => {
          const t = byId[a.tool_id];
          const def = defs[a.tool_id];
          if (!t) return (
            <li key={a.tool_id} className="rounded-2xl ring-1 ring-caution/40 p-4 flex items-center gap-3 text-[13.5px]">
              <span className="flex-1 text-fg-dim">Una herramienta que ya no existe o es de otro agente (id {a.tool_id}).</span>
              <Btn size="sm" variant="ghost" onClick={() => save(assigned.filter((_, j) => j !== i))} loading={busy}>Quitar</Btn>
            </li>
          );
          const slots = (def?.connectors || []).filter((s) => s.mode === 'agent');
          const missing = slots.some((s) => !a.connectors?.[s.slot]);
          return (
            <li key={a.tool_id} className="rounded-2xl bg-panel ring-1 ring-line p-4 sm:p-5">
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-[14.5px] font-semibold text-fg">
                    {t.name} <code className="text-[12px] font-normal text-fg-faint">{t.slug}</code>
                    <StatusTag>{t.scope === 'global' ? 'Global' : 'Personal'}</StatusTag>
                    {t.published_version ? <StatusTag tone="live" dot>v{t.published_version}</StatusTag> : <StatusTag tone="caution">Sin publicar: el agente no la ve</StatusTag>}
                    {missing && <StatusTag tone="caution">Falta elegir conector</StatusTag>}
                  </p>
                  <p className="mt-1 text-[13px] text-fg-dim">{def?.description || t.draft?.description}</p>
                  {(def?.instructions) && <p className="mt-1 text-[12.5px] text-fg-faint">Cómo la usa: {def.instructions}</p>}
                </div>
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => setEditing(t.id)} className="inline-flex items-center h-8 px-3.5 rounded-full text-[13px] font-semibold text-fg-dim hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">Editar</button>
                  <Btn size="sm" variant="ghost" icon={FaTimes} onClick={() => save(assigned.filter((_, j) => j !== i))} disabled={readOnly} loading={busy}>Quitar</Btn>
                </div>
              </div>
              {slots.length > 0 && (
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {slots.map((s) => (
                    <Field key={s.slot} label={s.label || s.slot} hint={usable(s.kind).length ? undefined : 'No hay conectores de este tipo para este cliente: agrégalo en Integraciones.'}>
                      <NativeSelect value={a.connectors?.[s.slot] || ''} disabled={readOnly || busy}
                        onChange={(e) => save(assigned.map((x, j) => (j === i ? { ...x, connectors: { ...(x.connectors || {}), [s.slot]: Number(e.target.value) || undefined } } : x)))}>
                        <option value="">Elige el conector</option>
                        {usable(s.kind).map((c) => <option key={c.id} value={c.id}>{c.name}{c.project_id === null ? ' (global)' : ''}</option>)}
                      </NativeSelect>
                    </Field>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <p className="text-[13px] text-fg-dim">¿Quieres que una herramienta corra siempre, antes de que el agente piense (por ejemplo, buscar a la persona por su teléfono)? Ponla en <Link to={`/agentes/${agent.id}/antes-de-responder`} className="text-live hover:underline">Antes de responder</Link>.</p>

      <AddToolModal open={adding} onClose={() => setAdding(false)} tools={available}
        onAdd={async (t) => { await save([...assigned, { tool_id: t.id, connectors: {} }]); setAdding(false); }}
        onFinish={(t) => { setAdding(false); setEditing(t.id); }} onCreate={() => { setAdding(false); setCreatingTool(true); }} />

      {/* Crear una herramienta sin salir del agente: queda asignada a este agente y se abre su editor encima de esta pantalla. */}
      <NewToolModal open={creatingTool} personalOnly={role === 'employee'} preset={{ agentId: agent.id }} agents={[{ ...agent, project: null }]} onClose={() => setCreatingTool(false)}
        onCreate={async (input) => {
          const t = await toolsService.createTool({ ...input, draft: emptyDraft() });
          toastSuccess('Herramienta creada');
          if (!assigned.some((a) => a.tool_id === t.id)) await onSave([...assigned, { tool_id: t.id, connectors: {} }]);
          setCreatingTool(false); setEditing(t.id);
        }} />

      {editing !== null && (
        <div className="fixed inset-0 z-[45] overflow-y-auto bg-ink p-4 sm:p-6" role="dialog" aria-modal="true" aria-label="Editor de la herramienta">
          <ToolEditor key={editing} toolId={editing} embedded backLabel={`Volver a ${agent.name}`} onExit={() => { setEditing(null); load(); }} />
        </div>
      )}
    </div>
  );
}

function AddToolModal({ open, onClose, tools, onAdd, onFinish, onCreate }) {
  const [q, setQ] = useState('');
  useEffect(() => { if (open) setQ(''); }, [open]);
  const rows = tools.filter((t) => !q.trim() || `${t.name} ${t.slug} ${t.draft?.description || ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Modal open={open} onClose={onClose} title="Agregar herramienta" description="Las globales y las personales de este agente. Una en borrador se puede agregar ya: el agente la usa cuando la publiques." width="max-w-xl"
      footer={<><Btn variant="ghost" onClick={onCreate} icon={FaPlus}>Crear una nueva</Btn><Btn variant="ghost" onClick={onClose}>Cerrar</Btn></>}>
      <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar" aria-label="Buscar herramientas" autoFocus />
      <ul className="mt-3 max-h-[50vh] overflow-y-auto divide-y divide-line rounded-xl ring-1 ring-line">
        {rows.map((t) => (
          <li key={t.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 text-[14px] font-semibold text-fg">
                {t.name}
                <span className="text-[12px] font-normal text-fg-faint">{t.scope === 'global' ? 'Global' : 'Personal'}</span>
                {t.published_version ? <StatusTag tone="live" dot>v{t.published_version}</StatusTag> : <StatusTag tone="caution">Borrador: aún no se publica</StatusTag>}
              </p>
              <p className="text-[12.5px] text-fg-dim truncate">{t.draft?.description || 'Sin descripción'}</p>
            </div>
            {!t.published_version && <button type="button" onClick={() => onFinish(t)} className="text-[12.5px] font-semibold text-live hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">Terminar y publicar</button>}
            <Btn size="sm" onClick={() => onAdd(t)}>Agregar</Btn>
          </li>
        ))}
        {!rows.length && <li className="px-4 py-5 text-[13.5px] text-fg-dim">{tools.length ? 'Ninguna coincide con la búsqueda.' : 'No hay herramientas todavía: usa «Crear una nueva».'}</li>}
      </ul>
    </Modal>
  );
}
